import React, { useState, useCallback, useEffect } from 'react';
import { Dialog, Box, IconButton, Typography, CircularProgress, Tooltip } from '@mui/material';
import { Close, Download as DownloadIcon } from '@mui/icons-material';
import axios from 'axios';
import Cookies from 'js-cookie';
// The SAME player and document viewer the live /pitch-reels page renders
// (views/pitch-reels/index.js imports this file), so the overlay and the Reels
// page are visually identical.
import { VideoItem, DisplayDocuments } from 'views/pitch-reels/components/VideoReelPlayer';
import CustomPopup from 'ui-components/popups/CustomPopup';
import { HandlePitchReelAction } from 'api/pitch-reels';
import { SaveView } from 'api/startup_applications';
import { trackEngagementEvent, VIEW_TYPES } from 'api/engagement';
import {
  checkForAuthorization,
  downloadFile,
  getReelLastViewedAt,
  setReelLastViewedAt,
  shouldRecordView,
} from 'utils/index';

// Plays a single startup's reel in a black full-screen overlay, so the Deal Flow
// list / board no longer navigate away to /pitch-reels. Same VideoItem the feed
// uses; next/previous are inert because this shows one reel only.
const ReelModal = ({
  open,
  onClose,
  item,
  userPermissions = {},
  addToast,
  isSelected = false,
  investor_id,
  // Mirrors where the old navigation sent you: the deal-flow feed for New rows,
  // the selected feed otherwise. The backend default ('preference') excludes
  // startups already in the pipeline, so it must be passed explicitly.
  viewType = 'deal-flow',
}) => {
  // Browsers block autoplay with sound - the feed starts muted for the same reason.
  const [isMuted, setIsMuted] = useState(true);
  const [video, setVideo] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [activeButtonsState, setActiveButtonsState] = useState({ interested: false, uninterested: false });
  // Deck viewer. The Reels page drives this off a ?view-document= search param,
  // but the overlay is not a route - local state keeps the reel open underneath,
  // so closing the deck returns straight to it.
  const [deckFile, setDeckFile] = useState(null);

  const { mutateAsync: pitchReelActionAsync, isLoading: isActionLoading } = HandlePitchReelAction();
  const { mutateAsync: SaveViewAsync } = SaveView();
  const investorId = investor_id || Cookies.get('X-INV-ID');
  // List rows carry the startup id on _id; KanbanCard maps startup_id onto _id too.
  const startupId = item?._id || item?.startup_id;

  useEffect(() => {
    if (!open || !startupId || !investorId) {
      setVideo(null);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setVideo(null);

    axios({
      method: 'get',
      url: `${process.env.REACT_APP_BASE_URL}/pitch-reel`,
      params: { investor_id: investorId, selectedStartupId: startupId, viewType, limit: 1 },
      headers: { Authorization: `Bearer ${checkForAuthorization()}` },
    })
      .then((res) => {
        if (cancelled) return;
        const reel = res?.data?.videos?.[0];
        if (!reel) {
          addToast && addToast('Reel not available for this startup.', 'error');
          onClose && onClose();
          return;
        }
        setVideo(reel);
        setActiveButtonsState({
          interested: Boolean(reel?.isLiked),
          uninterested: Boolean(reel?.isPassed),
        });
      })
      .catch(() => {
        if (cancelled) return;
        addToast && addToast('Could not load this reel.', 'error');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, startupId, investorId, viewType]);

  const handleToggleMute = useCallback(() => setIsMuted((m) => !m), []);
  const handleMuteVideo = useCallback(() => setIsMuted(true), []);

  const runAction = useCallback(async (action) => {
    const id = video?.startupId || video?._id || startupId;
    if (!id) return;
    try {
      await pitchReelActionAsync({ startupId: id, action, investorId });
    } catch (e) {
      addToast && addToast('Could not update this startup. Please try again.', 'error');
      setActiveButtonsState({ interested: false, uninterested: false });
    }
  }, [video, startupId, investorId, pitchReelActionAsync, addToast]);

  const handleInterested = useCallback(() => runAction('like'), [runAction]);
  const handleUninterested = useCallback(() => runAction('dislike'), [runAction]);

  // Same payload the Reels page writes to sessionStorage before opening its viewer.
  const handleOpenDeck = useCallback(() => {
    if (!video?.pitch_deck_path) return;

    // Counts towards the founder's Deck Views, same target as every other route
    // to the deck - so opening it here and from the profile on one day is still
    // a single view.
    trackEngagementEvent({
      startup_id: video?.startupId || startupId,
      investor_id: investorId,
      view_type: VIEW_TYPES.DECK_VIEW,
      target: 'pitch_deck',
    });

    setDeckFile({
      file_name: video?.startupName || 'Pitch Deck',
      startup_name: video?.startupName || 'Pitch Deck',
      url: video?.pitch_deck_path,
    });
  }, [video, startupId, investorId]);

  // VideoItem applies the 3s dwell rule and calls this once per reel; the feed
  // wires the identical handler, so the overlay records views the same way it
  // does rather than leaving onReelWatched a no-op.
  const handleReelWatched = useCallback((watchedVideo) => {
    const reel_id = watchedVideo?.id;
    const startup_id = watchedVideo?.startupId || watchedVideo?.id;
    if (!reel_id || !startup_id || !investorId) return;
    if (!shouldRecordView(getReelLastViewedAt(investorId, reel_id))) return;

    SaveViewAsync({ startup_id, investor_id: investorId, view_type: 'pitch_reel' })
      .then((response) => {
        if (response?.data?.investorInfo?.statusCode == 200) {
          setReelLastViewedAt(investorId, reel_id);
        }
      })
      .catch((err) => {
        addToast && addToast(err?.response?.data?.message, 'error');
      });
  }, [investorId, SaveViewAsync, addToast]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      // Translucent scrim instead of a solid backdrop, so the Deal Flow list /
      // board stays visible (blurred) behind the reel.
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: 'rgba(16,24,40,0.55)',
            backdropFilter: 'blur(10px)',
          },
        },
      }}
      PaperProps={{
        sx: { backgroundColor: 'transparent', backgroundImage: 'none', boxShadow: 'none' },
      }}
    >
      <Box
        // Clicking the scrim around the reel closes it; clicks on the player and
        // its controls land on children, so they are left alone.
        onClick={(e) => { if (e.target === e.currentTarget) onClose && onClose(); }}
        sx={{
          position: 'relative',
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <IconButton
          onClick={onClose}
          aria-label="Close reel"
          sx={{
            position: 'absolute',
            top: '1rem',
            right: '1rem',
            zIndex: 30,
            color: '#fff',
            backgroundColor: 'rgba(0,0,0,0.5)',
            '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' },
          }}
        >
          <Close />
        </IconButton>

        {isLoading && <CircularProgress sx={{ color: '#fff' }} />}

        {!isLoading && video && (
          <VideoItem
            video={video}
            isActive
            isMuted={isMuted}
            onToggleMute={handleToggleMute}
            onMuteVideo={handleMuteVideo}
            onInterested={handleInterested}
            onUninterested={handleUninterested}
            onPreviousReel={() => { }}
            onNextReel={() => { }}
            currentIndex={0}
            isEndCardVisible={false}
            isSelected={isSelected}
            isActionLoading={isActionLoading}
            activeButtonsState={activeButtonsState}
            setActiveButtonsState={setActiveButtonsState}
            userPermissions={userPermissions}
            addToast={addToast}
            handleOpenDocument={handleOpenDeck}
            onReelWatched={handleReelWatched}
            forceMobileActions
          />
        )}

        {!isLoading && !video && (
          <Typography sx={{ color: 'rgba(255,255,255,0.7)' }}>Reel unavailable.</Typography>
        )}
      </Box>

      {Boolean(deckFile) && (
        <CustomPopup
          open={Boolean(deckFile)}
          handleClose={() => setDeckFile(null)}
          title={
            <>
              {deckFile?.file_name}
              <Tooltip title="Download">
                <IconButton
                  size="medium"
                  sx={{ position: 'absolute', top: '0.5rem', right: '3rem' }}
                  onClick={() => downloadFile(deckFile?.url, (deckFile?.startup_name || 'Startup') + '_pitchdeck')}
                  aria-label="more options"
                >
                  <DownloadIcon fontSize="medium" />
                </IconButton>
              </Tooltip>
            </>
          }
          titleSx={{ marginBottom: { xs: '0rem', md: '2rem' }, marginTop: { xs: '2rem', md: '0rem' } }}
          sx={{ paddingTop: '0rem', placeContent: 'stretch', height: '100%' }}
          key="File Popup"
        >
          <DisplayDocuments type={deckFile?.url?.split('.')?.pop()?.toLowerCase()} url={deckFile?.url} />
        </CustomPopup>
      )}
    </Dialog>
  );
};

export default ReelModal;
